/**
 * Consolidated information about unresolved/missing template elements
 * Only included when genMisMetaInfo config is enabled
 */
export class MissingMetaInfo {
    nonPlaceholders: MissingPlaceholderInfo[] = [];
    nonSlotholders: MissingSlotholderInfo[] = [];
    nonJsonKeys: MissingJsonKeyInfo[] = [];
    nonJsonArrayKeys: MissingJsonKeyInfo[] = [];
}

/**
 * Information about an unresolved placeholder
 */
export class MissingPlaceholderInfo {
    placeholderName: string = '';
    templateName: string = '';
    templateAppSite: string = '';
    templatePath: string = '';
}

/**
 * Information about an unresolved slotholder
 */
export class MissingSlotholderInfo {
    slotholderName: string = '';
    templateName: string = '';
    templateAppSite: string = '';
    templatePath: string = '';
}

/**
 * Information about an unresolved JSON key
 */
export class MissingJsonKeyInfo {
    jsonKey: string = '';
    templateName: string = '';
    templateAppSite: string = '';
    templatePath: string = '';
}

/**
 * AppSite metadata - purely hierarchical structure
 */
export class AppSiteMetadata {
    appSiteName: string = '';
    appFile: string = '';
    searchAppSites: string = '';
    appView: string = '';
    appViews: string[] = []; // List of AppView names discovered from Views/ folder
    rootComponent: TemplateInfo | null = null;
    missingInfo: MissingMetaInfo | null = null; // Only populated when genMisMetaInfo is enabled
}

/**
 * Template information and metadata
 */
export class TemplateInfo {
    htmlFileName: string = '';
    htmlRelativePath: string = '';
    jsonFileName: string = ''; // Associated JSON data file (if exists)
    placeholderName: string = ''; // Name in parent template (e.g., "MainContent" in {{MainContent}})
    templateName: string = ''; // Actual template name (e.g., "NextContent" for NextContent.html)
    templateAppSite: string = ''; // AppSite where template is defined
    appFileName: string = ''; // Root AppFile name for DynamicView content type detection (propagated from root)
    templateType: string = ''; // "PlaceHolder", "Normal", "Slotholder", "Slotted", etc.
    placeholders: string[] = [];
    nonPlaceholders: string[] = [];
    appViewPlaceholders: Map<string, string> = new Map(); // Key: placeholder name, Value: component name
    slotholders: SlotInfo[] = [];
    nonSlotholders: SlotInfo[] = [];
    slots: string[] = [];
    jsonKeys: string[] = []; // JSON key references from {{$KeyName}}
    jsonInheritKeys: Map<string, string> = new Map(); // JSON keys with inheritance (key# in JSON) - Key: JsonKey, Value: Source TemplateName
    jsonArrayKeys: string[] = []; // Array iteration keys from {{@ArrayKey}} - also includes boolean conditionals
    nonJsonKeys: string[] = []; // Unresolved JSON keys
    nonJsonArrayKeys: string[] = []; // Unresolved JSON array keys
    conditionalKeys: string[] = []; // Conditional block keys from {{@ConditionalKey}}
    components: TemplateInfo[] = [];
    hasDynamicData: boolean = false; // Set by populateDynamicHandlerFlags
    hasDynamicView: boolean = false; // Set by populateDynamicHandlerFlags
    isStaticHtmlOnly: boolean = false;
    hasJsonKeys: boolean = false;
    hasPlaceholders: boolean = false;
    hasSlotholders: boolean = false;
    hasArrayBlocks: boolean = false;
    hasConditionals: boolean = false;
    requiresDataJsonMerge: boolean = false;
    requiresContextMerge: boolean = false;
    requiresTemplateTraversal: boolean = true;
    canReturnStaticHtml: boolean = false;
    entries: TemplateEntryInfo[] = [];
    jsonKeyEntries: JsonKeyEntryInfo[] = [];
}

/**
 * Precomputed execution entry for one top-level HtmlJson key.
 * HtmlJson remains the authoritative ordered payload; this metadata indexes it.
 */
export class TemplateEntryInfo {
    htmlJsonKey: string = '';
    entryType: string = '';
    name: string = '';
    ordinal: number = 0;
    isSkipMarked: boolean = false;
    hasNestedContent: boolean = false;
}

/**
 * Precomputed metadata for a JsonKey HtmlJson entry.
 */
export class JsonKeyEntryInfo {
    htmlJsonKey: string = '';
    jsonKey: string = '';
    isSkipMarked: boolean = false;
    isInherited: boolean = false;
}

/**
 * Slot information
 */
export class SlotInfo {
    name: string = '';
    slots: SlotContentInfo[] = [];
}

/**
 * Slot content information
 */
export class SlotContentInfo {
    name: string = '';
    slotholders: SlotInfo[] = [];
}
